import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Header, Dropdown, Icon, Flag } from 'semantic-ui-react';
import cn from 'classnames';

import { Portfolio, ComparePortfolio } from 'actions';
import { colors } from 'utils/colors';
import { withRouter } from 'react-router-dom';
import Fade from 'react-reveal/Fade';
import { ErrorLabel, Button, Loading } from 'components';

class ExistingPortfolioSelection extends Component {
  state = {
    id: null,
    error: null,
  };

  componentDidMount() {
    const { dispatch, analytics } = this.props;
    const { data } = analytics;
    dispatch(Portfolio.fetchModel());
    dispatch(Portfolio.fetchUser());
    dispatch(
      ComparePortfolio.convertAnalyticsPortfolioObject(
        data.portfolio,
        data.paramsAnalyzed,
        true
      )
    );
  }

  componentDidUpdate() {
    const { comparePortfolioState, setView } = this.props;
    if (comparePortfolioState.compareResult) setView('Result');
  }

  getPortfolios = (portfolios) => {
    const { id } = this.state;
    const { toggle_region } = this.props;
    const selectedId = id ? id.id : null;

    return portfolios
      .filter((port) => {
        if (toggle_region === 'US') {
          return port.is_usd;
        }
        return true;
      })
      .map((portfolio) => ({
        key: portfolio.id,
        value: { id: portfolio.id, client: portfolio.client },
        selected: portfolio.id === selectedId,
        text: (
          <span
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '8px',
            }}
          >
            <Icon name="briefcase" />
            <Flag name={portfolio.is_usd ? 'us' : 'ca'} />
            {portfolio.name}
          </span>
        ),
      }));
  };

  handleChange = (e, { value }) => {
    const { dispatch } = this.props;
    dispatch(ComparePortfolio.fetchId(value.id, false));

    this.setState({ id: value, error: null });
  };

  decideRegion = () => {
    const { user_region, toggle_region } = this.props;
    if (user_region === 'US') {
      return 'US';
    }
    if (toggle_region === 'US') {
      return 'US';
    }
    return 'CA';
  };

  submit = () => {
    const { dispatch, comparePortfolioState, analytics } = this.props;
    const { paramsPassed } = analytics.data || {};

    const { portfolio, original, gic_info } = comparePortfolioState;

    const customBenchmark =
      analytics && analytics.benchmark === 'custom'
        ? paramsPassed
          ? paramsPassed.custom_bm
          : null
        : null;
    const rebalanceInterval =
      analytics.data.paramsPassed.rebalance_interval || 1;

    return dispatch(
      ComparePortfolio.fetchCompare(
        original.portfolio,
        portfolio,
        original.gic_info,
        gic_info,
        this.decideRegion(),
        customBenchmark,
        paramsPassed.check_history || false,
        rebalanceInterval
      )
    );
  };

  isCompareButtonDisabled = () => {
    const { comparePortfolioState } = this.props;
    const { id } = this.state;
    return id === null || comparePortfolioState.isFetching;
  };

  render() {
    const { portfolios, comparePortfolioState, french, toggle_region } =
      this.props;
    const { error, id } = this.state;
    const { isFetching } = comparePortfolioState;
    const formattedPortfolioList = this.getPortfolios(portfolios);

    return (
      <div>
        {isFetching ? <Loading style={{ top: '-3rem' }} /> : null}
        <Fade>
          <StyledDiv>
            <CompareContent>
              <Header
                size="medium"
                content={
                  french
                    ? 'Sélectionnez un portefeuille à comparer'
                    : 'Select a Portfolio to Compare'
                }
                style={{ margin: 0 }}
              />
              <Header.Subheader
                style={{
                  textAlign: 'justify',
                  maxWidth: '48rem',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  marginTop: '1rem',
                  marginBottom: '2rem',
                }}
                content={
                  french
                    ? 'Tous les portefeuilles enregistrés apparaîtront dans la liste déroulante ci-dessous'
                    : 'All saved portfolios will appear in the dropdown below'
                }
              >
                <Icon name="warning sign" style={{ color: colors.orange }} />
                {french
                  ? "La période de comparaison est définie par l'histoire commune la plus longue des deux portefeuilles que vous comparez. Par conséquent, les scores du portefeuille actuel peuvent changer, car son histoire peut maintenant être plus courte."
                  : 'The comparison period is set to the longest common history of the two portfolios you are comparing. Therefore, the scores of the current portfolio may change, as its history may now be shorter.'}
                <br />
                <Icon
                  name="warning sign"
                  style={{ marginTop: '1rem', color: colors.orange }}
                />
                {french
                  ? "Les scores de performance et de risque à la baisse de Wealthscope sont déterminés par rapport au benchmark d'un portefeuille. Par conséquent, vous devriez ignorer ces scores et ne comparer que les métriques financières si les deux portefeuilles ont une allocation d'actifs différente, correspondant à un benchmark différent."
                  : 'The Wealthscope Performance and Downside Risk scores are determined relative to a portfolio’s benchmark. As such, you should ignore these scores and compare only the financial metrics if the two portfolios have a different asset allocation, corresponding to a different benchmark.'}
              </Header.Subheader>
              {formattedPortfolioList.length === 0 && (
                <div
                  style={{
                    marginBottom: '5px',
                    color: 'red',
                    margin: 'auto',
                    maxWidth: '48rem',
                  }}
                >
                  {french
                    ? "Vous n'avez actuellement aucun portefeuille enregistré."
                    : `You currently have no saved portfolios${
                        toggle_region === 'US'
                          ? ' in which all holdings are dominated in USD'
                          : ''
                      }.`}
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '48rem',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
              >
                <Dropdown
                  fluid
                  selection
                  disabled={portfolios.length === 0}
                  placeholder="Comparison Portfolio..."
                  options={formattedPortfolioList}
                  text={
                    id !== null &&
                    portfolios.find((port) => port.id === id.id).name
                  }
                  selectOnBlur={false}
                  onChange={this.handleChange}
                  style={{ width: '60%' }}
                />
                <ErrorLabel msg={error} />
                <Button
                  className={cn('left', {
                    disabled: this.isCompareButtonDisabled(),
                  })}
                  icon="line chart"
                  color={colors.darkBlue}
                  margin="1rem 0"
                  onClick={this.submit}
                >
                  {french ? 'Comparer' : 'Compare'}
                </Button>
              </div>
            </CompareContent>
          </StyledDiv>
        </Fade>
      </div>
    );
  }
}

ExistingPortfolioSelection.propTypes = {
  analytics: PropTypes.object,
  setView: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  portfolios: PropTypes.array,
  comparePortfolioState: PropTypes.object,
  user_region: PropTypes.string,
  toggle_region: PropTypes.string,
  french: PropTypes.bool,
};

ExistingPortfolioSelection.defaultProps = {
  portfolios: [],
  comparePortfolioState: {},
  analytics: {},
  user_region: 'CA',
  toggle_region: 'CA',
  french: false,
};

export default withRouter(
  connect((state) => ({
    comparePortfolioState: state.ComparePortfolio,
    portfolios: state.Portfolio.user,
    analytics: state.Analytics,
    compareEsg: state.Analytics.compareEsg,
    esgToggle: state.Analytics.esgToggle,
    user_region: state.Storage.user_region,
    toggle_region: state.Storage.toggle_region || 'CA',
    french: state.Storage.language === 'fr',
  }))(ExistingPortfolioSelection)
);

const StyledDiv = styled.div`
  & {
    // border: 2px solid ${colors.blue};
    // border-radius: 4px;

    width: 100%;
    height: 100%;
    display: flex;
    padding: 3rem 0;
    margin-bottom: 9rem;
    flex-direction: column;
    justify-content: flex-start;
    & > div {
      display: flex;
      flex-direction: column;
      align-items: center;
      div.search {
        .results {
          .category {
            align-items: flex-start;
          }
        }
      }
    }
    div.compare-whatif-toggles {
      width: 50%;
      display: block;
    }
  }
`;

const CompareContent = styled.div`
  position: relative;
  padding: 2rem 2rem 1rem 2rem;
  text-align: ${(props) => (props.centered ? 'center' : 'left')};
`;
